import { useQuery } from "@tanstack/react-query"
import { routes, type MarketplaceId } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"

/** Snapshots are appended once a day by the `nova offers sync` cron, so a short
 *  refetch cadence buys nothing — keep them fresh for minutes. */
const OFFER_HEALTH_STALE_MS = 5 * 60 * 1000

/** One persisted offer-health observation for a marketplace — the COUNT subset
 *  of the API's OfferHealthSnapshotRow (the row also carries `byFacet`, which
 *  only the CLI read-back uses today). `byInactivityReason` is a histogram over
 *  the inactive offers — an offer with several reasons counts in each, so its
 *  sum can exceed `inactive`. */
export type OfferHealthSnapshot = {
  marketplace: MarketplaceId
  capturedAt: string
  active: number
  /** Missing on an older API; null when historical counts were not recorded. */
  unknown?: number | null
  inactive: number
  total: number
  byInactivityReason: Record<string, number>
}

/** Latest offer-health snapshot for every marketplace that has one. The
 *  marketplaces dashboard reads this once and indexes it per card. */
export function useOfferHealthRollup() {
  return useQuery({
    queryKey: ["offer-health", "rollup"] as const,
    staleTime: OFFER_HEALTH_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<{ marketplaces: OfferHealthSnapshot[] }>(
        routes.marketplaces.offerHealth()
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.marketplaces
    },
  })
}

export type OfferHealthDetail = {
  marketplace: MarketplaceId
  latest: OfferHealthSnapshot | null
  trend: OfferHealthSnapshot[]
}

/** One marketplace's current snapshot plus its recent trend (oldest→newest). */
export function useOfferHealth(marketplace: MarketplaceId) {
  return useQuery({
    queryKey: ["offer-health", "detail", marketplace] as const,
    staleTime: OFFER_HEALTH_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<OfferHealthDetail>(
        routes.marketplaces.offerHealthByMarketplace({ marketplace })
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}
